import { ControlPosition } from "./controls/BaseControl.js";

export interface StateValue<T> {
    disabled?: T;
    selected?: T;
    active?: T;
    default?: T;
};

export type StateValueString = string | StateValue<string>;

export interface PanoButtonOptions {
    icon?: StateValueString;
    text?: StateValueString;
    html?: StateValueString;
    tooltip?: StateValueString;
    position?: ControlPosition;
    active?: boolean;
    selected?: boolean;
    visible?: boolean;
    disabled?: boolean;
    onInit?: (item: PanoButton) => void;
    onClick?: (item: PanoButton) => void;
}

export class PanoButton {

    public onClick = e5.Signal.create<[PanoButton]>();
    public onVisibilityChanged = e5.Signal.create<[PanoButton]>();

    public element: HTMLElement;

    private _options: PanoButtonOptions = null;

    constructor(options: PanoButtonOptions) {

        options = e5.object.merge({
            active: false,
            selected: false,
            disabled: false,
            position: ControlPosition.RIGHT,
            visible: true
        }, options);
        this._options = options;

        this.element = document.createElement("div");
        this.element.className = "pano-button";
        this.element.classList.toggle("selected", options.selected);
        this.element.classList.toggle("disabled", options.disabled);
        this.element.classList.toggle("active", options.active);
        this.element.classList.toggle("hidden", !options.visible);

        this.element.addEventListener("click", () => {
            this.onClick.dispatch(this);
        });

        if (typeof options.onClick === "function") {
            this.onClick.add(options.onClick);
            this.clickable = true;
        }

        this.element.addEventListener("keydown", (e) => {
            if (e.repeat) {
                return;
            }
            if (e5.KeyCode.is(e, e5.KeyCode.SPACE, e5.KeyCode.ENTER)) {
                this.onClick.dispatch(this);
            }
        });

        this.visible = options.visible;
        this.render();

        if (typeof options.onInit === "function") {
            options.onInit(this);
        }
    }

    private render(): void {

        this.element.innerHTML = "";

        const options = this._options;

        const icon = this.getStateValueString(options.icon);
        if (icon) {
            const e = document.createElement("div");
            e.className = "pano-button__icon";
            e.innerHTML = icon ? `<i class="${icon}"></i>` : "";
            this.element.appendChild(e);
        }

        const text = this.getStateValueString(options.text);
        if (text) {
            const e = document.createElement("div");
            e.className = "pano-button__title";
            e.textContent = text || "";
            this.element.appendChild(e);
        }

        const html = this.getStateValueString(options.html);
        if (html) {
            const e = document.createElement("div");
            e.className = "pano-button__html";
            e.innerHTML = html || "";
            this.element.appendChild(e);
        }

        const tooltip = this.getStateValueString(options.tooltip);
        if (tooltip) {
            this.element.setAttribute("data-tooltip", tooltip);
        } else {
            this.element.removeAttribute("data-tooltip");
        }
    }

    private getStateValueString(value: string | StateValue<string>): string | null {
        if (typeof value === "string") {
            return value;
        }
        return this.getStateValue(value);
    }

    private getStateValue<T = any>(value: StateValue<T>): T | null {
        if (!value) {
            return null;
        }
        if (value.disabled !== undefined && this.disabled) {
            return value.disabled;
        }
        else if (value.active !== undefined && this.active) {
            return value.active;
        }
        else if (value.default !== undefined) {
            return value.default;
        }
        return null;
    }

    private invalidateRender(): void {
        this.render();
    }

    public set icon(icon: StateValueString) {
        this._options.icon = icon;
        this.invalidateRender();
    }

    public set text(text: StateValueString) {
        this._options.text = text;
        this.invalidateRender();
    }

    public set html(html: StateValueString) {
        this._options.html = html;
        this.invalidateRender();
    }

    public get clickable(): boolean {
        return this.element.classList.contains("pano-button--clickable");
    }

    public set clickable(v: boolean) {
        if (this.clickable == v) {
            return;
        }
        this.element.classList.toggle("pano-button--clickable", v);
        if (v) {
            this.element.setAttribute("tabindex", "0");
        } else {
            this.element.removeAttribute("tabindex");
        }
    }

    public get active(): boolean {
        return this.element.classList.contains("pano-button--active");
    }

    public set active(v: boolean) {
        if (this.active == v) {
            return;
        }
        this.element.classList.toggle("pano-button--active", v);
        this.invalidateRender();
    }

    public get selected(): boolean {
        return this.element.classList.contains("pano-button--selected");
    }

    public set selected(v: boolean) {
        if (this.selected == v) {
            return;
        }
        this.element.classList.toggle("pano-button--selected", v);
        this.invalidateRender();
    }

    public get disabled(): boolean {
        return this.element.classList.contains("pano-button--disabled");
    }

    public set disabled(v: boolean) {
        if (this.disabled == v) {
            return;
        }
        this.element.classList.toggle("pano-button--disabled", v);
        this.invalidateRender();
    }

    public get visible(): boolean {
        return !this.element.classList.contains("hidden");
    }

    public set visible(v: boolean) {
        if (this.visible == v) {
            return;
        }
        this.element.classList.toggle("hidden", !v);
        this.onVisibilityChanged.dispatch(this);
    }
}